import { CmsCategory } from '@xolv-mybrightlink/common';
import { CmsEvent } from '../models/EventResult';

export const initialCategoryState = {
  initial: true,
  events: [],
  endCursor: null,
  error: false,
  loading: false,
};

export type CategoryState = {
  initial: boolean;
  events: CmsEvent[];
  endCursor: string | null;
  error: boolean;
  loading: boolean;
};

type EventDetails = {
  databaseId?: number;
  data: 'loading' | 'error' | CmsEvent;
};

export type EventState =
  | {
      initial: true;
    }
  | {
      initial: false;
      categories: CmsCategory[];
      data: Record<string, CategoryState>;
      eventDetails: EventDetails[];
    };

export type EventAction =
  | {
      type: 'category-fetched';
      categories: CmsCategory[];
    }
  | { type: 'event-list-fetching'; categoryId: string }
  | {
      type: 'event-list-error';
      categoryId: string;
    }
  | {
      type: 'event-list-fetched';
      categoryId: string;
      events: CmsEvent[];
      endCursor: string | null;
    }
  | { type: 'event-fetching'; databaseId?: number }
  | {
      type: 'event-error';
      databaseId?: number;
    }
  | {
      type: 'event-fetched';
      event: CmsEvent;
    }
  | {
      type: 'bookmark';
      databaseId: number;
    };

const isEventDetails = (
  event: EventDetails,
  databaseId: number | undefined,
) => {
  return event.databaseId === databaseId && !!databaseId;
};

export const findEventDetails = (
  events: EventDetails[],
  databaseId: number | undefined,
) => {
  return events.find((event) => isEventDetails(event, databaseId));
};

export const EventReducer = (
  state: EventState,
  action: EventAction,
): EventState => {
  if (action.type === 'category-fetched') {
    return {
      initial: false,
      categories: action.categories,
      data: {},
      eventDetails: [],
    };
  }
  if (state.initial) {
    return state;
  }
  if (action.type === 'bookmark') {
    const data = Object.entries(state.data).reduce((result, entry) => {
      const [categoryId, categoryState] = entry;
      const events = categoryState.events.map((event) => {
        if (event.databaseId === action.databaseId) {
          return { ...event, isSaved: !event.isSaved };
        }
        return event;
      });
      result[categoryId] = { ...categoryState, events };
      return result;
    }, {} as Record<string, CategoryState>);
    const eventDetails = state.eventDetails.map((event) => {
      if (isEventDetails(event, action.databaseId)) {
        const data =
          typeof event.data === 'string'
            ? event.data
            : { ...event.data, isSaved: !event.data.isSaved };
        return { ...event, data };
      }
      return event;
    });
    return { ...state, data, eventDetails };
  } else if (action.type === 'event-list-fetching') {
    const categoryId = action.categoryId;
    const categoryState = state.data[categoryId] ?? initialCategoryState;
    return {
      ...state,
      data: {
        ...state.data,
        [action.categoryId]: { ...categoryState, loading: true },
      },
    };
  } else if (action.type === 'event-list-error') {
    const categoryId = action.categoryId;
    const categoryState = state.data[categoryId] ?? initialCategoryState;
    return {
      ...state,
      data: {
        ...state.data,
        [action.categoryId]: { ...categoryState, error: true },
      },
    };
  } else if (action.type === 'event-list-fetched') {
    const categoryId = action.categoryId;
    const categoryState = state.data[categoryId] ?? initialCategoryState;
    return {
      ...state,
      data: {
        ...state.data,
        [action.categoryId]: {
          ...categoryState,
          initial: false,
          loading: false,
          events: [...categoryState.events, ...action.events],
          endCursor: action.endCursor,
        },
      },
    };
  } else if (action.type === 'event-fetching') {
    const event = findEventDetails(state.eventDetails, action.databaseId);
    if (!event) {
      return {
        ...state,
        eventDetails: [
          ...state.eventDetails,
          { databaseId: action.databaseId, data: 'loading' },
        ],
      };
    }
  } else if (action.type === 'event-error') {
    return {
      ...state,
      eventDetails: state.eventDetails.map((event) => {
        if (isEventDetails(event, action.databaseId)) {
          return { ...event, data: 'error' };
        }
        return event;
      }),
    };
  } else if (action.type === 'event-fetched') {
    const fetchedArticle = action.event;
    return {
      ...state,
      eventDetails: state.eventDetails.map((event) => {
        if (isEventDetails(event, fetchedArticle.databaseId)) {
          return {
            databaseId: fetchedArticle.databaseId,
            slug: fetchedArticle.slug,
            data: action.event,
          };
        }
        return event;
      }),
    };
  }
  return state;
};
